import { ReactNode } from "react";
import { useTheme } from "styled-components";
import { IconType } from "react-icons";
import { FaChevronDown, FaTimes } from "react-icons/fa";
import { FilterPopover } from "./FilterPopover";

/**
 * Default table-filter pill: an `icon + label: value` trigger that opens a
 * dropdown panel holding the filter control. Bespoke filters (e.g. the date
 * range) can supply their own pill instead via `barPillComponent`.
 */
export const FilterBarPill = ({
  icon: Icon,
  label,
  valueLabel,
  isActive,
  isOpen,
  onToggle,
  onClose,
  onClear,
  panelWidth,
  children,
}: {
  icon?: IconType;
  label: string;
  valueLabel?: string;
  isActive?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onClear?: () => void;
  panelWidth?: number;
  children: ReactNode;
}) => {
  const theme = useTheme();

  return (
    <FilterPopover
      isOpen={isOpen}
      onClose={onClose}
      panelWidth={panelWidth}
      renderTrigger={({ ref }) => (
        <button
          type="button"
          ref={ref}
          onClick={onToggle}
          className="flex items-center px-3 text-xs border rounded-md h-9 whitespace-nowrap hover:bg-gray3"
          style={{
            borderColor: theme.borderColor,
          }}
        >
          {Icon ? (
            <Icon className="flex-shrink-0 mr-2 text-muted" size={14} />
          ) : null}
          <span className="font-medium leading-none text-muted">{label}</span>
          {valueLabel ? (
            <span
              className="ml-1 font-medium leading-none truncate"
              style={{ maxWidth: 160, color: theme.textColorPrimary }}
            >
              {valueLabel}
            </span>
          ) : null}
          {onClear && isActive ? (
            <span
              role="button"
              tabIndex={0}
              aria-label={`Clear ${label}`}
              onClick={(event) => {
                event.stopPropagation();
                onClear();
              }}
              className="flex items-center justify-center flex-shrink-0 w-4 h-4 ml-2 rounded cursor-pointer text-muted hover:text-gray12"
            >
              <FaTimes size={10} />
            </span>
          ) : null}
          <FaChevronDown
            className="flex-shrink-0 ml-2 transition-transform text-muted"
            size={10}
            style={{ transform: isOpen ? "rotate(180deg)" : undefined }}
          />
        </button>
      )}
    >
      {children}
    </FilterPopover>
  );
};
